import { initSentry } from "@/lib/sentry";

initSentry();

import { StrictMode } from "react";

import { I18nProvider } from "@lingui/react";
import { configureAuthClient } from "@pontimed/auth-client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { LazyMotion, MotionConfig, domMax } from "framer-motion";
import { Provider as JotaiProvider } from "jotai";
import { createRoot } from "react-dom/client";

import { hydrateInitialLocale, i18n } from "@/i18n/i18n";
import { DeferredPostHog } from "@/lib/analytics/deferred-posthog";
import { nativeSessionStore } from "@/lib/native-session-store";
import { ThemeProvider } from "@/lib/theme/theme-provider";

import { routeTree } from "./routeTree.gen";

import "@/styles/globals.css";

/**
 * Recover from stale lazy chunks after a deploy. When a code-split chunk's
 * hashed filename no longer exists on the server (a newer build replaced it),
 * the dynamic `import()` 404s → Cloudflare Pages serves index.html (text/html)
 * → the module script fails its strict MIME check. Vite fires
 * `vite:preloadError`; we reload ONCE to pull the fresh shell + manifest. A
 * short sessionStorage guard prevents reload loops if the chunk is genuinely
 * broken (then the error surfaces to the route error boundary instead).
 */
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "pm:preload-reload-at";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return; // already reloaded recently — let it surface
  event.preventDefault();
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

/**
 * Resolve apps/api base URL. NEXT_PUBLIC_API_URL is canonical (architecture
 * rule 1, baked into the Cloudflare Pages prod build). VITE_API_URL is the
 * Vite-native fallback so dev `.env.local` files using either name work.
 * Empty string = same-origin (Vite proxy or Caddy reverse-proxy forwards
 * `/api` to apps/api).
 */
const API_BASE_URL = import.meta.env.NEXT_PUBLIC_API_URL ?? import.meta.env.VITE_API_URL ?? "";

/**
 * PostHog config — same env-var conventions as the API base URL. The
 * provider becomes a no-op when the key is unset (CI / local dev without
 * analytics) so this is safe to mount unconditionally.
 */
const POSTHOG_KEY =
  import.meta.env.NEXT_PUBLIC_POSTHOG_KEY ?? import.meta.env.VITE_POSTHOG_KEY ?? undefined;
const POSTHOG_HOST =
  import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ?? import.meta.env.VITE_POSTHOG_HOST ?? undefined;

configureAuthClient({
  apiBaseUrl: API_BASE_URL,
  // The Capacitor build keeps its own tokens (no cookies in its WebView);
  // undefined on the web.
  nativeSession: nativeSessionStore(),
  onAuthFailure: () => {
    // Defer to the _authenticated layout: it watches session status and,
    // on `unauthenticated`, opens the auth modal in place over a skeleton.
    // No URL change needed — the user stays on the page they were trying
    // to use, and a successful sign-in re-renders the same route with the
    // real Outlet.
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 5 minutes default — most account / dashboard data is fine to read
      // from cache between navigations. Volatile per-page data (search
      // results, real-time appointment status) should override per-query
      // with `staleTime: 0` or short windows. Reference data (specialties,
      // countries, feature catalog) overrides upward with `Infinity`.
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // `defaultPreloadStaleTime: 0` makes the preloaded `loader` always run on
  // hover/intent — but our loaders call `queryClient.ensureQueryData`, which
  // itself returns the cached value when fresh. Net effect: preload fires
  // the network request only when React Query's cache is stale.
  defaultPreloadStaleTime: 0,
  // Inject the React Query client into every loader via `context`. Route
  // files read it as `loader: ({ context }) => context.queryClient...`
  // and call `ensureQueryData` to prefetch in parallel with the route
  // chunk download — by the time the component mounts, data is hot.
  context: { queryClient },
  // Scroll restoration is disabled because /find uses search params for
  // filter state — every toggle would otherwise re-trigger restoration
  // and produce a brief 0-then-restore flicker on desktop. Browser-default
  // scroll behavior is fine for our nav patterns (filter clicks stay put;
  // cross-page nav uses `resetScroll: false` per call).
  scrollRestoration: false,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root not found");

void hydrateInitialLocale().finally(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <JotaiProvider>
        <ThemeProvider>
          <I18nProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
              <DeferredPostHog
                apiKey={POSTHOG_KEY}
                apiHost={POSTHOG_HOST}
                debug={import.meta.env.DEV}
                appName="portal"
                environment={import.meta.env.DEV ? "dev" : "prod"}
              >
                <MotionConfig reducedMotion="user">
                  {/* LazyMotion drives the `m.*` components throughout the
                       app (the codemod swapped every `motion.div` for
                       `m.div`); without this wrapper they silently no-op.
                       `domMax`, not `domAnimation`: the portal drags (sheet
                       drag-to-dismiss, /find list↔map swipe, carousels,
                       swipeable tabs) and `domAnimation` has no drag. With it
                       every drag was dead until @pontimed/provider-ui's
                       `motion.*` registered the full bundle globally — then
                       the already-mounted /find swipe `m.div` mounted a drag
                       gesture with no projection node and crashed the route.
                       NOT `strict`: the shared packages (provider-ui,
                       feedback-ui) render `motion.*` because apps/web has no
                       LazyMotion, and strict throws on them in dev (prod never
                       threw — the invariant is dev-only). */}
                  <LazyMotion features={domMax}>
                    <RouterProvider router={router} />
                  </LazyMotion>
                </MotionConfig>
              </DeferredPostHog>
            </QueryClientProvider>
          </I18nProvider>
        </ThemeProvider>
      </JotaiProvider>
    </StrictMode>,
  );
});
